import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { FileText, Users, UploadCloud, Image as ImageIcon, Package, Fuel, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_LABELS = [
  { key: 'today',    label: 'Today' },
  { key: 'mtd',      label: 'MTD' },
  { key: 'ytd',      label: 'YTD' },
  { key: 'all_time', label: 'All' },
];

/**
 * My KPIs — daily activity summary for the logged-in clerk/admin.
 * Shows: loans captured, drivers onboarded, statements uploaded,
 * parts images captured, stock loaded.
 */
export default function ClerkKPIWidget() {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  const fetchKpi = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clerk-workbench/kpis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKpi(res.data);
    } catch {}
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchKpi();
    const iv = setInterval(fetchKpi, 60000);
    return () => clearInterval(iv);
  }, [fetchKpi]);

  const tiles = [
    { key: 'loans_captured',       label: 'Loans Captured',      icon: FileText,    color: 'from-blue-500 to-blue-600' },
    { key: 'drivers_onboarded',    label: 'Drivers Onboarded',   icon: Users,       color: 'from-emerald-500 to-emerald-600' },
    { key: 'statements_uploaded',  label: 'Statements Uploaded', icon: UploadCloud, color: 'from-purple-500 to-purple-600' },
    { key: 'parts_images_captured',label: 'Parts Images',        icon: ImageIcon,   color: 'from-amber-500 to-amber-600' },
    { key: 'stock_loaded',         label: 'Stock Loaded',        icon: Package,     color: 'from-rose-500 to-rose-600' },
    { key: 'fuel_captured',        label: 'Fuel Captured',       icon: Fuel,        color: 'from-cyan-500 to-cyan-600' },
  ];

  const getBucket = (metricKey, bucketKey) => kpi?.buckets?.[metricKey]?.[bucketKey] ?? 0;

  return (
    <Card className="mb-4" data-testid="clerk-kpi-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-500" />
            <h3 className="font-semibold">My KPIs</h3>
            {kpi?.totals && (
              <div className="flex flex-wrap gap-1 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-600 border border-indigo-500/40 font-bold">
                  Today: {kpi.totals.today}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-600 border border-sky-500/40 font-semibold">
                  MTD: {kpi.totals.mtd}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 border border-emerald-500/40 font-semibold">
                  YTD: {kpi.totals.ytd}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/40 font-semibold">
                  All-time: {kpi.totals.all_time}
                </span>
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchKpi} title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-3">Loading KPIs...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
            {tiles.map(({ key, label, icon: Icon, color }) => (
              <div
                key={key}
                className={`relative overflow-hidden rounded-lg bg-gradient-to-br ${color} text-white p-3`}
                data-testid={`kpi-${key}`}
              >
                <Icon className="absolute right-2 top-2 h-5 w-5 opacity-40" />
                <div className="text-[10px] uppercase tracking-wider opacity-90 pr-6">{label}</div>
                <div className="text-2xl font-extrabold mt-1" data-testid={`kpi-${key}-today`}>
                  {getBucket(key, 'today')}
                </div>
                <div className="text-[9px] uppercase opacity-80 leading-tight">Today</div>
                <div className="grid grid-cols-3 gap-1 mt-2 pt-2 border-t border-white/25">
                  {BUCKET_LABELS.slice(1).map(({ key: bk, label: bl }) => (
                    <div key={bk} className="min-w-0">
                      <div className="text-[9px] uppercase opacity-70 leading-none">{bl}</div>
                      <div className="text-sm font-bold leading-tight" data-testid={`kpi-${key}-${bk}`}>
                        {getBucket(key, bk)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
